import { useState, type FormEvent } from 'react';
import { useMutation } from '@tanstack/react-query';
import { api, errorMessage, tokens, type AuthUser } from '@/lib/api';
import { Badge, Button, Field, Input, Spinner } from '@/components/ui';

interface SetupStatus {
  needs_setup: boolean;
  version: string;
  database_ready: boolean;
  mock_mode: { vicidial: boolean; telephony: boolean; ai: boolean };
}

interface SetupResult {
  ok: boolean;
  version: string;
  access_token: string;
  refresh_token: string;
  user: AuthUser;
  demo: { tenant_id: string; bots: number; vicidial_users: number } | null;
}

const MIN_PASSWORD = 12;

/**
 * First-run wizard. Only reachable while the platform has no super admin.
 *
 * The setup token is written to disk by the installer and printed at the end of
 * the install — it exists so that nobody who merely reaches the server before
 * you do can claim the platform.
 */
export default function Setup({
  status,
  onComplete,
}: {
  status: SetupStatus;
  onComplete: (user: AuthUser) => void;
}) {
  const [setupToken, setSetupToken] = useState('');
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [createDemo, setCreateDemo] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const complete = useMutation({
    mutationFn: () =>
      api.post<SetupResult>('/setup/complete', {
        setup_token: setupToken.trim(),
        email,
        name,
        password,
        create_demo_client: createDemo,
      }),
    onSuccess: (result) => {
      // The server signs us in as part of completing setup.
      tokens.set(result.access_token, result.refresh_token, result.user);
      onComplete(result.user);
    },
    onError: (err) =>
      setError(errorMessage(err, 'Setup failed. Check the logs.')),
  });

  const tooShort = password !== '' && password.length < MIN_PASSWORD;
  const mismatch = confirm !== '' && password !== confirm;
  const ready =
    setupToken.trim() !== '' &&
    email !== '' &&
    name !== '' &&
    password.length >= MIN_PASSWORD &&
    password === confirm;

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    if (!ready) return;
    complete.mutate();
  };

  return (
    <div className="flex min-h-full items-center justify-center bg-sunken px-4 py-12">
      <div className="w-full max-w-lg">
        <div className="mb-6 text-center">
          <span className="mx-auto mb-3 grid h-11 w-11 place-items-center rounded-xl bg-brand-600 text-lg font-bold text-white">
            G
          </span>
          <h1 className="text-xl font-semibold text-ink-900 dark:text-ink-50">
            Set up Glee AI Bot
          </h1>
          <p className="mt-1 text-sm text-ink-500 dark:text-ink-400">
            Create the platform owner account. This runs once.
          </p>
          <div className="mt-2 flex justify-center gap-1.5">
            <Badge tone="blue">v{status.version}</Badge>
            <Badge tone={status.database_ready ? 'green' : 'red'}>
              {status.database_ready ? 'Database ready' : 'Database not migrated'}
            </Badge>
          </div>
        </div>

        {!status.database_ready && (
          <div className="mb-4 rounded-md bg-red-50 px-4 py-3 text-sm text-red-800 ring-1 ring-inset ring-red-200 dark:bg-red-950/40 dark:text-red-300 dark:ring-red-900">
            The schema is not installed. Run{' '}
            <code className="font-mono text-xs">sudo glee-migrate</code> on the server
            before continuing.
          </div>
        )}

        <form
          onSubmit={onSubmit}
          className="space-y-4 rounded-lg bg-surface p-6 shadow-sm ring-1 ring-line"
        >
          {error && (
            <div
              role="alert"
              className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700 ring-1 ring-inset ring-red-200 dark:bg-red-950/50 dark:text-red-300 dark:ring-red-900"
            >
              {error}
            </div>
          )}

          <Field
            label="Setup token"
            required
            hint="Printed at the end of the installer. Recover it with: sudo cat /opt/glee/.setup-token"
          >
            <Input
              value={setupToken}
              onChange={(e) => setSetupToken(e.target.value)}
              placeholder="Paste the token from the server"
              autoComplete="off"
              autoFocus
              className="font-mono"
            />
          </Field>

          <div className="border-t border-line pt-4">
            <Field label="Your name" required>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Platform Owner"
                autoComplete="name"
              />
            </Field>
          </div>

          <Field label="Email" required hint="You will sign in with this.">
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="username"
            />
          </Field>

          <Field
            label="Password"
            required
            hint={`At least ${MIN_PASSWORD} characters.`}
            error={tooShort ? `Needs ${MIN_PASSWORD} characters or more` : undefined}
          >
            <Input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="new-password"
            />
          </Field>

          <Field
            label="Confirm password"
            required
            error={mismatch ? 'Passwords do not match' : undefined}
          >
            <Input
              type="password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              autoComplete="new-password"
            />
          </Field>

          <label className="flex items-start gap-2 border-t border-line pt-4 text-sm text-ink-700 dark:text-ink-200">
            <input
              type="checkbox"
              checked={createDemo}
              onChange={(e) => setCreateDemo(e.target.checked)}
              className="mt-0.5 rounded border-ink-300"
            />
            <span>
              Create the demo client
              <span className="block text-xs text-ink-500 dark:text-ink-400">
                ABC Home Services with 10 bots, 10 Vicidial users and their assignments,
                so there is something to click through. Safe to delete later.
              </span>
            </span>
          </label>

          <Button
            type="submit"
            className="w-full"
            disabled={!ready}
            loading={complete.isPending}
          >
            {complete.isPending ? 'Setting up…' : 'Create account and finish'}
          </Button>
        </form>

        <div className="mt-4 rounded-md bg-sunken px-4 py-3 text-xs text-ink-600 dark:text-ink-300">
          <p className="font-medium text-ink-800 dark:text-ink-100">After setup</p>
          <p className="mt-1">
            The platform starts in mock mode — no dialer, no vendor accounts, no spend.
            Turn integrations on one at a time from{' '}
            <code className="font-mono">/opt/glee/.env</code>, verifying each. The setup
            token is destroyed once this completes.
          </p>
        </div>
      </div>
    </div>
  );
}

export function SetupLoading() {
  return (
    <div className="flex min-h-full items-center justify-center gap-2 bg-sunken text-sm text-ink-500">
      <Spinner /> Checking installation…
    </div>
  );
}
